import { useEffect } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import rgfLogo from '@/assets/rgf-logo.png';
import { setInviteUnauthorizedHandler } from './services/inviteApiClient';

export const SUPPORT_WHATSAPP_NUMBER = '7347012411';
const SUPPORT_WHATSAPP_INTL = `91${SUPPORT_WHATSAPP_NUMBER}`;
const SUPPORT_WHATSAPP_DEFAULT_MESSAGE =
  "Hi, I need help with my Roundglass Foundation onboarding link.";

export const buildSupportWhatsAppUrl = (message?: string): string => {
  const text = encodeURIComponent(message || SUPPORT_WHATSAPP_DEFAULT_MESSAGE);
  return `https://wa.me/${SUPPORT_WHATSAPP_INTL}?text=${text}`;
};

/**
 * Branded shell for the public candidate-onboarding portal.
 *
 * Intentionally renders OUTSIDE the backoffice AppLayout — no sidebar, no
 * header, no Redux-backed user menu. Surfaces only:
 *   - RGF logo + "Candidate Onboarding" title
 *   - the routed page body
 *   - a footer with the WhatsApp support contact
 *
 * Also installs a global 401-handler on the invite client that redirects to
 * the expired screen. The handler is scoped to mount/unmount so the backoffice
 * never inherits this redirect behavior.
 */
export const CandidatePortalLayout = () => {
  const navigate = useNavigate();

  useEffect(() => {
    setInviteUnauthorizedHandler(() => {
      navigate('/onboarding/invite/expired', { replace: true });
    });
    return () => {
      setInviteUnauthorizedHandler(null);
    };
  }, [navigate]);

  return (
    <div className="min-h-screen flex flex-col bg-rgf-gray-50 overflow-x-hidden">
      <header className="bg-white border-b border-rgf-gray-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center gap-3">
          <img
            src={rgfLogo}
            alt="Roundglass Foundation"
            className="h-7 sm:h-9 w-auto max-w-[120px] sm:max-w-[180px] object-contain flex-shrink-0"
          />
          <div className="min-w-0 flex-1">
            <h1 className="text-sm sm:text-lg font-semibold text-rgf-gray-900 leading-tight truncate">
              Candidate Onboarding
            </h1>
            <p className="text-xs text-rgf-gray-500 truncate">Roundglass Foundation</p>
          </div>
        </div>
      </header>

      <main className="flex-1 w-full">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
          <Outlet />
        </div>
      </main>

      <footer className="bg-white border-t border-rgf-gray-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 text-xs text-rgf-gray-600">
          <p className="break-words">
            Need help? Reach our support team on WhatsApp at{' '}
            <a
              href={buildSupportWhatsAppUrl()}
              target="_blank"
              rel="noreferrer"
              className="text-rgf-primary-700 hover:text-rgf-primary-800 hover:underline font-medium whitespace-nowrap"
            >
              +91 {SUPPORT_WHATSAPP_NUMBER}
            </a>
            .
          </p>
          <p className="mt-1 text-rgf-gray-400">
            © {new Date().getFullYear()} Roundglass Foundation. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};
